import { type VariantProps, cva } from 'class-variance-authority'

// Tour Content Variants
export const tourContentVariants = cva('tour-content-base bg-background rounded-lg max-w-sm shadow-none', {
  variants: {
    intent: {
      welcome: '',
      action: 'bg-blue-50/50 dark:bg-blue-950/50',
      info: 'bg-background',
      success: 'bg-green-50/50 dark:bg-green-950/50',
      warning: 'bg-orange-50/50 dark:bg-orange-950/50',
    },
  },
  defaultVariants: {
    intent: 'info',
  },
})

// Title Variants
export const tourTitleVariants = cva('font-heading font-semibold text-foreground mb-2', {
  variants: {
    size: {
      small: 'text-base',
      default: 'text-lg',
      large: 'text-xl',
    },
  },
  defaultVariants: {
    size: 'default',
  },
})

// Description Variants
export const tourDescriptionVariants = cva('text-muted-foreground leading-relaxed', {
  variants: {
    size: {
      small: 'text-xs',
      default: 'text-sm',
      large: 'text-base',
    },
  },
  defaultVariants: {
    size: 'default',
  },
})

// Highlight Variants
export const tourHighlightVariants = cva('text-xs font-medium', {
  variants: {
    variant: {
      primary: 'text-primary',
      info: 'text-blue-600 dark:text-blue-400',
      success: 'text-green-600 dark:text-green-400',
      warning: 'text-orange-600 dark:text-orange-400',
      action: 'text-blue-600 dark:text-blue-400',
    },
  },
  defaultVariants: {
    variant: 'primary',
  },
})

// Badge Variants
export const tourBadgeVariants = cva('inline-flex items-center px-2 py-1 text-xs font-medium rounded-full', {
  variants: {
    variant: {
      step: 'bg-primary/10 text-primary border border-primary/20',
      status: 'bg-secondary text-secondary-foreground',
      progress: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
    },
  },
  defaultVariants: {
    variant: 'step',
  },
})

// Button Variants (extends existing buttonVariants)
export const tourButtonVariants = cva(
  'inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none',
  {
    variants: {
      variant: {
        primary: 'bg-primary text-primary-foreground hover:bg-primary/90',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        outline: 'border border-input hover:bg-accent hover:text-accent-foreground',
      },
      size: {
        sm: 'h-8 px-3 text-xs',
        default: 'h-9 px-4',
        lg: 'h-10 px-6',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'default',
    },
  },
)

// Types
export interface TourContentProps extends VariantProps<typeof tourContentVariants> {
  children: React.ReactNode
  className?: string
}

export interface TourTitleProps extends VariantProps<typeof tourTitleVariants> {
  children: React.ReactNode
  className?: string
}

export interface TourDescriptionProps extends VariantProps<typeof tourDescriptionVariants> {
  children: React.ReactNode
  className?: string
}

export interface TourHighlightProps extends VariantProps<typeof tourHighlightVariants> {
  children: React.ReactNode
  className?: string
}

export interface TourBadgeProps extends VariantProps<typeof tourBadgeVariants> {
  children: React.ReactNode
  className?: string
}

export interface TourButtonProps extends VariantProps<typeof tourButtonVariants> {
  children: React.ReactNode
  className?: string
  onClick?: () => void
  disabled?: boolean
  type?: 'button' | 'submit' | 'reset'
}
